import { useState } from 'react';

/**
 * Dialog component for handling warehouse conflicts
 * Shows when user tries to create a warehouse that already exists
 */
export default function WarehouseConflictDialog({ 
  warehouseName, 
  existingWarehouse, 
  onOverride, 
  onUpdate, 
  onCancel 
}) {
  const [selectedOption, setSelectedOption] = useState('update'); // 'override' or 'update'

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 border border-gray-700 shadow-2xl">
        <h2 className="text-xl font-bold text-white mb-2">Warehouse Already Exists</h2>
        <p className="text-gray-300 mb-4">
          A warehouse named <span className="font-semibold text-blue-400">"{warehouseName}"</span> already exists.
          How would you like to proceed?
        </p>

        {existingWarehouse && (
          <div className="bg-gray-900/50 rounded-lg p-3 mb-4 border border-gray-700">
            <div className="text-sm text-gray-400 mb-1">Existing Warehouse Info:</div>
            <div className="text-sm text-gray-200">
              <div>Name: {existingWarehouse.display_name || existingWarehouse.name}</div>
              {existingWarehouse.gps_polygon && (
                <div>Has GPS coordinates: Yes</div>
              )}
              {existingWarehouse.blueprint?.outlinePoints && (
                <div>Has outline: Yes ({existingWarehouse.blueprint.outlinePoints.length} points)</div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-3 mb-6">
          <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-700/50 transition-colors"
                 style={{ borderColor: selectedOption === 'update' ? '#3b82f6' : '#4b5563' }}>
            <input
              type="radio"
              name="action"
              value="update"
              checked={selectedOption === 'update'}
              onChange={(e) => setSelectedOption(e.target.value)}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-medium text-white mb-1">Update Existing</div>
              <div className="text-sm text-gray-400">
                Merge new data with existing warehouse. Only updates fields you provide (outline, GPS coordinates).
                Preserves existing zones, assets, and other data.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-700/50 transition-colors"
                 style={{ borderColor: selectedOption === 'override' ? '#ef4444' : '#4b5563' }}>
            <input
              type="radio"
              name="action"
              value="override"
              checked={selectedOption === 'override'}
              onChange={(e) => setSelectedOption(e.target.value)}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-medium text-white mb-1">Override Completely</div>
              <div className="text-sm text-gray-400">
                Replace all warehouse data with new information. This will update the outline and GPS coordinates,
                but will preserve zones and assets (they will remain linked to this warehouse).
              </div>
            </div>
          </label>
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (selectedOption === 'override') {
                onOverride();
              } else {
                onUpdate();
              }
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedOption === 'override'
                ? 'bg-red-600 hover:bg-red-500 text-white'
                : 'bg-blue-600 hover:bg-blue-500 text-white'
            }`}
          >
            {selectedOption === 'override' ? 'Override' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
}




